/**
 * BrainPage — Dashboard del Poker Brain
 *
 * Muestra:
 *  - KPIs: manos enriquecidas, signals totales, alerts brain vs legacy
 *  - Breakdown por detector con max/avg confidence
 *  - Tabla de alerts source=poker_brain con filtros (severity, alert_type)
 *  - Acciones: "Correr detectores ahora", "Promover signals → alerts"
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Brain, Play, RefreshCw, ShieldAlert, Database, Sparkles, AlertTriangle, X as XIcon, FileText, ExternalLink, Search, Users, User as UserIcon, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import KpiCard from "@/modules/collector_v2/components/common/KpiCard";

// KpiCard expects: title, value, description, icon, testid (named export "KpiCard")
const _KpiCard = (props) => (
  <div data-testid={props.testid} style={{
    padding: 16, border: "1px solid hsl(var(--border))", borderRadius: 10,
    background: "hsl(var(--card))", display: "flex", justifyContent: "space-between",
    alignItems: "center", gap: 12,
  }}>
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, textTransform: "uppercase", color: "hsl(var(--muted-foreground))", letterSpacing: 0.4 }}>
        {props.label}
      </span>
      <strong style={{ fontSize: 22, lineHeight: 1 }}>{props.value}</strong>
      <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>{props.hint}</span>
    </div>
    <div style={{ background: "hsl(var(--primary) / 0.15)", padding: 10, borderRadius: 8 }}>
      {props.icon ? <props.icon className="h-5 w-5" style={{ color: "hsl(var(--primary))" }} /> : null}
    </div>
  </div>
);

const SEV_COLORS = {
  critical: "destructive",
  high: "destructive",
  medium: "secondary",
  low: "outline",
};

const SEV_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };

function apiUrl(path) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // Backend pppoker-v2 escucha en otro puerto, asumimos proxy ingress lo enruta.
  // En VPS los endpoints v2 viven bajo /api/v2/.
  if (path.startsWith("/")) return `${base}${path}`;
  return `${base}/${path}`;
}

async function fetchJSON(path, opts = {}) {
  const res = await fetch(apiUrl(path), {
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.json();
}

export default function BrainPage() {
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [filterSev, setFilterSev] = useState("");
  const [filterType, setFilterType] = useState("");
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [lastAction, setLastAction] = useState(null);
  const [drawerAlert, setDrawerAlert] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Investigation tab state
  const [tab, setTab] = useState("dashboard"); // dashboard | investigate
  const [invMode, setInvMode] = useState("pair");
  const [invUids, setInvUids] = useState(["", ""]);
  const [invLoading, setInvLoading] = useState(false);
  const [invResult, setInvResult] = useState(null);
  const [invError, setInvError] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const d = await fetchJSON("/api/v2/brain/overview");
      setOverview(d);
    } catch (e) {
      setError(`Overview: ${e.message}`);
    }
  }, []);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ limit: "200" });
      if (filterSev) qs.set("severity", filterSev);
      if (filterType) qs.set("alert_type", filterType);
      const d = await fetchJSON(`/api/v2/brain/alerts?${qs.toString()}`);
      setAlerts(d.items || []);
      setAlertsTotal(d.total || 0);
    } catch (e) {
      setError(`Alerts: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [filterSev, filterType]);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const runDetectors = async () => {
    setRunning(true);
    setLastAction(null);
    try {
      const r = await fetchJSON("/api/v2/brain/detectors/run", {
        method: "POST",
        body: JSON.stringify({}),
      });
      // Promover automáticamente
      const p = await fetchJSON("/api/v2/brain/alerts/promote", { method: "POST" });
      setLastAction({
        kind: "run",
        signals: r?.result?.total_signals ?? 0,
        promoted: p?.promotion?.upserted ?? 0,
        elapsed_s: r?.result?.elapsed_total_s ?? null,
        at: new Date().toLocaleTimeString(),
      });
      await loadOverview();
      await loadAlerts();
    } catch (e) {
      setError(`Run: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const validateGolden = async () => {
    setRunning(true);
    try {
      const seed = await fetchJSON("/api/v2/brain/golden/seed", { method: "POST" });
      const v = await fetchJSON("/api/v2/brain/golden/validate", { method: "POST" });
      await fetchJSON("/api/v2/brain/golden/cleanup", { method: "POST" });
      setLastAction({
        kind: "golden",
        pass: v?.summary?.pass ?? 0,
        active: v?.summary?.active_cases ?? 0,
        passRate: v?.summary?.pass_rate_on_active ?? 0,
        at: new Date().toLocaleTimeString(),
      });
    } catch (e) {
      setError(`Golden: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const openAlertDetail = async (alert) => {
    setDrawerAlert({ ...alert, _detail: null });
    setDrawerLoading(true);
    try {
      const d = await fetchJSON(`/api/v2/brain/alerts/${encodeURIComponent(alert.alert_id)}`);
      setDrawerAlert({ ...alert, _detail: d });
    } catch (e) {
      setDrawerAlert({ ...alert, _detail: { error: e.message } });
    } finally {
      setDrawerLoading(false);
    }
  };

  const closeDrawer = () => setDrawerAlert(null);

  // ─── Investigación ──────────────────────────────────────────────────────
  const setInvMode_ = (m) => {
    setInvMode(m);
    if (m === "single") setInvUids([""]);
    else if (m === "pair") setInvUids(["", ""]);
    else if (m === "group") setInvUids(["", "", ""]);
    setInvResult(null);
    setInvError(null);
  };
  const updateInvUid = (idx, val) => {
    const copy = [...invUids];
    copy[idx] = val.replace(/[^0-9]/g, "");
    setInvUids(copy);
  };
  const addInvUid = () => setInvUids([...invUids, ""]);
  const removeInvUid = (idx) => {
    const copy = invUids.filter((_, i) => i !== idx);
    setInvUids(copy);
  };

  const launchInvestigation = async () => {
    setInvLoading(true);
    setInvError(null);
    setInvResult(null);
    try {
      const cleanUids = invUids.map((u) => parseInt(u, 10)).filter((n) => Number.isFinite(n));
      if (invMode === "single" && cleanUids.length !== 1) {
        throw new Error("Modo single: ingresá exactamente 1 UID");
      }
      if (invMode === "pair" && cleanUids.length !== 2) {
        throw new Error("Modo par: ingresá exactamente 2 UIDs");
      }
      if (invMode === "group" && cleanUids.length < 3) {
        throw new Error("Modo grupo: ingresá al menos 3 UIDs");
      }
      const r = await fetchJSON("/api/v2/brain/investigate", {
        method: "POST",
        body: JSON.stringify({ mode: invMode, uids: cleanUids }),
      });
      if (!r.ok) throw new Error(r.error || "investigación falló");
      setInvResult(r);
    } catch (e) {
      setInvError(e.message);
    } finally {
      setInvLoading(false);
    }
  };

  const sortedDetectors = useMemo(() => {
    const list = overview?.signals?.by_detector || [];
    return [...list].sort((a, b) => b.count - a.count);
  }, [overview]);

  const alertTypes = useMemo(() => {
    const set = new Set();
    alerts.forEach((a) => a.alert_type && set.add(a.alert_type));
    return [...set].sort();
  }, [alerts]);

  return (
    <div data-testid="brain-page" style={{ padding: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Brain className="h-8 w-8" style={{ color: "#0ea5e9" }} />
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }} data-testid="brain-title">
              Poker Brain
            </h1>
            <p style={{ fontSize: 13, color: "hsl(var(--muted-foreground))", margin: 0 }}>
              Motor antifraude determinístico · 15 detectores tácticos · scheduler cada 15 min
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            data-testid="brain-refresh-btn"
            variant="outline"
            onClick={() => { loadOverview(); loadAlerts(); }}
            disabled={loading}
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refrescar
          </Button>
          <Button
            data-testid="brain-run-detectors-btn"
            onClick={runDetectors}
            disabled={running}
          >
            <Play className="h-4 w-4 mr-2" />
            {running ? "Corriendo..." : "Correr detectores ahora"}
          </Button>
          <Button
            data-testid="brain-golden-validate-btn"
            variant="secondary"
            onClick={validateGolden}
            disabled={running}
          >
            <Sparkles className="h-4 w-4 mr-2" /> Validar Golden Cases
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid hsl(var(--border))" }}>
        <button
          data-testid="tab-dashboard"
          onClick={() => setTab("dashboard")}
          style={{
            padding: "10px 16px", border: "none", background: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 600,
            color: tab === "dashboard" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
            borderBottom: tab === "dashboard" ? "2px solid hsl(var(--primary))" : "2px solid transparent",
          }}
        >
          📊 Dashboard
        </button>
        <button
          data-testid="tab-investigate"
          onClick={() => setTab("investigate")}
          style={{
            padding: "10px 16px", border: "none", background: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 600,
            color: tab === "investigate" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
            borderBottom: tab === "investigate" ? "2px solid hsl(var(--primary))" : "2px solid transparent",
          }}
        >
          🔍 Investigar
        </button>
        <button
          data-testid="tab-confirmed-cases"
          onClick={() => setTab("confirmed-cases")}
          style={{
            padding: "10px 16px", border: "none", background: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 600,
            color: tab === "confirmed-cases" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
            borderBottom: tab === "confirmed-cases" ? "2px solid hsl(var(--primary))" : "2px solid transparent",
          }}
        >
          🏷️ Casos Confirmados
        </button>
        <button
          data-testid="tab-linked-devices"
          onClick={() => setTab("linked-devices")}
          style={{
            padding: "10px 16px", border: "none", background: "none",
            cursor: "pointer", fontSize: 13, fontWeight: 600,
            color: tab === "linked-devices" ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
            borderBottom: tab === "linked-devices" ? "2px solid hsl(var(--primary))" : "2px solid transparent",
          }}
        >
          🔗 Dispositivos Vinculados
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div data-testid="brain-error" style={{
          background: "hsl(0 70% 50% / 0.1)", border: "1px solid hsl(0 70% 50% / 0.3)",
          padding: 12, borderRadius: 8, marginBottom: 16, display: "flex",
          alignItems: "center", gap: 8, color: "hsl(0 70% 60%)",
        }}>
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {/* Last action banner */}
      {lastAction?.kind === "run" && (
        <div data-testid="brain-last-action-run" style={{
          background: "hsl(140 50% 50% / 0.1)", border: "1px solid hsl(140 50% 50% / 0.3)",
          padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14,
        }}>
          ✅ Detectores corridos a las {lastAction.at} —{" "}
          <strong>{lastAction.signals}</strong> señales en{" "}
          {lastAction.elapsed_s}s. <strong>{lastAction.promoted}</strong> alertas
          actualizadas/creadas.
        </div>
      )}
      {lastAction?.kind === "golden" && (
        <div data-testid="brain-last-action-golden" style={{
          background: "hsl(200 70% 50% / 0.1)", border: "1px solid hsl(200 70% 50% / 0.3)",
          padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 14,
        }}>
          🧪 Golden Cases validados a las {lastAction.at} —{" "}
          <strong>{lastAction.pass}/{lastAction.active}</strong> PASS ({lastAction.passRate}%)
        </div>
      )}

      {/* ═══════════════════ DASHBOARD TAB ═══════════════════ */}
      {tab === "dashboard" && (<>
      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginBottom: 20 }}>
        <_KpiCard
          testid="kpi-enriched"
          icon={Database}
          label="Manos enriquecidas"
          value={(overview?.enriched?.total ?? 0).toLocaleString()}
          hint={`${(overview?.enriched?.double_board ?? 0).toLocaleString()} double board`}
        />
        <_KpiCard
          testid="kpi-scoops"
          icon={Sparkles}
          label="Scoops detectados"
          value={(overview?.enriched?.scoops ?? 0).toLocaleString()}
          hint="DB scoops totales"
        />
        <_KpiCard
          testid="kpi-signals"
          icon={Brain}
          label="Señales (exploit_signals)"
          value={overview?.signals?.total ?? 0}
          hint={`${overview?.signals?.by_detector?.length ?? 0} detectores activos`}
        />
        <_KpiCard
          testid="kpi-alerts-brain"
          icon={ShieldAlert}
          label="Alertas Brain"
          value={overview?.alerts?.by_source?.poker_brain ?? 0}
          hint={`Legacy: ${overview?.alerts?.by_source?.legacy ?? 0}`}
        />
      </div>

      {/* Breakdown por detector */}
      <Card style={{ marginBottom: 20 }}>
        <CardHeader>
          <CardTitle style={{ fontSize: 16 }}>Señales por detector</CardTitle>
        </CardHeader>
        <CardContent>
          <div data-testid="detector-breakdown" style={{
            display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 10
          }}>
            {sortedDetectors.length === 0 && (
              <div style={{ color: "hsl(var(--muted-foreground))", fontSize: 13 }}>
                Sin señales todavía. Corré los detectores con el botón "Correr detectores ahora".
              </div>
            )}
            {sortedDetectors.map((d) => (
              <div
                key={d.signal_id}
                data-testid={`detector-row-${d.signal_id}`}
                style={{
                  padding: 12, border: "1px solid hsl(var(--border))",
                  borderRadius: 8, background: "hsl(var(--muted) / 0.3)",
                }}
              >
                <div style={{ fontSize: 12, fontFamily: "monospace", marginBottom: 4 }}>
                  {d.signal_id}
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                  <span style={{ fontSize: 24, fontWeight: 700 }}>{d.count}</span>
                  <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
                    señales
                  </span>
                </div>
                <div style={{ fontSize: 11, marginTop: 4, color: "hsl(var(--muted-foreground))" }}>
                  conf max {d.max_confidence}% · avg {d.avg_confidence}%
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filtros + tabla de alerts */}
      <Card>
        <CardHeader style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}>
          <CardTitle style={{ fontSize: 16 }}>
            Alertas del Brain
            <span style={{ fontSize: 12, fontWeight: 400, color: "hsl(var(--muted-foreground))", marginLeft: 8 }}>
              ({alertsTotal} total · mostrando {alerts.length})
            </span>
          </CardTitle>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              data-testid="filter-severity"
              value={filterSev}
              onChange={(e) => setFilterSev(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6, fontSize: 13, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))" }}
            >
              <option value="">Todas las severidades</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <select
              data-testid="filter-alert-type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{ padding: "6px 10px", borderRadius: 6, fontSize: 13, border: "1px solid hsl(var(--border))", background: "hsl(var(--background))" }}
            >
              <option value="">Todos los tipos</option>
              {alertTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </CardHeader>
        <CardContent>
          {loading && <div style={{ padding: 12, fontSize: 13 }}>Cargando...</div>}
          {!loading && alerts.length === 0 && (
            <div style={{ padding: 12, color: "hsl(var(--muted-foreground))", fontSize: 13 }}>
              No hay alertas con los filtros actuales.
            </div>
          )}
          {!loading && alerts.length > 0 && (
            <div data-testid="alerts-table" style={{ overflow: "auto" }}>
              <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid hsl(var(--border))" }}>
                    <th style={{ textAlign: "left", padding: 8 }}>Severidad</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Tipo</th>
                    <th style={{ textAlign: "left", padding: 8 }}>UIDs</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Risk</th>
                    <th style={{ textAlign: "right", padding: 8 }}>Conf</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Decisión</th>
                    <th style={{ textAlign: "left", padding: 8 }}>Evidencia</th>
                    <th style={{ textAlign: "center", padding: 8, width: 30 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.alert_id} data-testid={`alert-row-${a.alert_id}`}
                        onClick={() => openAlertDetail(a)}
                        style={{ borderBottom: "1px solid hsl(var(--border) / 0.5)", cursor: "pointer" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "hsl(var(--muted) / 0.4)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}>
                      <td style={{ padding: 8 }}>
                        <Badge variant={SEV_COLORS[a.severity] || "outline"}>
                          {a.severity}
                        </Badge>
                      </td>
                      <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{a.alert_type}</td>
                      <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>
                        {(a.uid_set || []).slice(0, 4).join(" · ")}
                        {(a.uid_set?.length || 0) > 4 ? ` +${a.uid_set.length - 4}` : ""}
                      </td>
                      <td style={{ padding: 8, textAlign: "right", fontWeight: 600 }}>{a.risk_score}</td>
                      <td style={{ padding: 8, textAlign: "right" }}>{a.confidence_score}%</td>
                      <td style={{ padding: 8, fontSize: 12 }}>{a.decision}</td>
                      <td style={{ padding: 8, fontSize: 12, color: "hsl(var(--muted-foreground))", maxWidth: 380 }}>
                        {(a.narrative_title || a.explanation || "").slice(0, 130)}
                        {(a.narrative_title || a.explanation || "").length > 130 ? "…" : ""}
                      </td>
                      <td style={{ padding: 8 }}>
                        <FileText className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* ═══════════════════ INVESTIGATE TAB ═══════════════════ */}
      {tab === "investigate" && (
        <div data-testid="investigate-tab">
          <Card style={{ marginBottom: 16 }}>
            <CardHeader>
              <CardTitle style={{ fontSize: 16, display: "flex", alignItems: "center", gap: 8 }}>
                <Search className="h-4 w-4" /> Investigación Dirigida
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Selector de modo */}
              <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
                {[
                  { v: "single", label: "UID individual", Icon: UserIcon },
                  { v: "pair", label: "Par (2 UIDs)", Icon: Users },
                  { v: "group", label: "Grupo (3+ UIDs)", Icon: Users },
                ].map(({ v, label, Icon }) => (
                  <button
                    key={v}
                    data-testid={`inv-mode-${v}`}
                    onClick={() => setInvMode_(v)}
                    style={{
                      padding: "10px 14px", borderRadius: 8,
                      border: invMode === v ? "2px solid hsl(var(--primary))" : "1px solid hsl(var(--border))",
                      background: invMode === v ? "hsl(var(--primary) / 0.1)" : "hsl(var(--background))",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 13,
                      fontWeight: invMode === v ? 600 : 400,
                    }}
                  >
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>

              {/* Inputs UIDs */}
              <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
                UIDs a investigar:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                {invUids.map((u, idx) => (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <input
                      data-testid={`inv-uid-input-${idx}`}
                      value={u}
                      onChange={(e) => updateInvUid(idx, e.target.value)}
                      placeholder="ej. 12345678"
                      style={{
                        padding: "8px 10px", borderRadius: 6,
                        border: "1px solid hsl(var(--border))",
                        background: "hsl(var(--background))",
                        fontFamily: "monospace", fontSize: 13, width: 140,
                      }}
                    />
                    {invMode === "group" && invUids.length > 3 && (
                      <button
                        onClick={() => removeInvUid(idx)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "hsl(0 70% 60%)" }}
                      ><XIcon className="h-4 w-4" /></button>
                    )}
                  </div>
                ))}
                {invMode === "group" && (
                  <button
                    data-testid="inv-add-uid"
                    onClick={addInvUid}
                    style={{
                      padding: "8px 12px", borderRadius: 6, fontSize: 12,
                      border: "1px dashed hsl(var(--border))",
                      background: "none", cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 4,
                    }}
                  >
                    <Plus className="h-3 w-3" /> Agregar UID
                  </button>
                )}
              </div>

              <Button
                data-testid="inv-launch-btn"
                onClick={launchInvestigation}
                disabled={invLoading}
                size="lg"
              >
                {invLoading ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Investigando (puede tardar ~90s)…</>
                ) : (
                  <><Search className="h-4 w-4 mr-2" /> Lanzar investigación</>
                )}
              </Button>

              {invError && (
                <div data-testid="inv-error" style={{
                  marginTop: 12, padding: 10, borderRadius: 6, fontSize: 13,
                  background: "hsl(0 70% 50% / 0.1)", border: "1px solid hsl(0 70% 50% / 0.3)",
                  color: "hsl(0 70% 60%)",
                }}>⚠️ {invError}</div>
              )}
            </CardContent>
          </Card>

          {/* Resultado */}
          {invResult && (
            <div data-testid="inv-result">
              {/* Score banner */}
              <Card style={{ marginBottom: 12 }}>
                <CardContent style={{ paddingTop: 20 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 20, alignItems: "center" }}>
                    <div style={{
                      width: 110, height: 110, borderRadius: "50%",
                      background: scoreToColor(invResult.summary.collusion_score),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexDirection: "column",
                    }}>
                      <div style={{ fontSize: 30, fontWeight: 800, color: "white" }}>
                        {invResult.summary.collusion_score}
                      </div>
                      <div style={{ fontSize: 10, color: "white", opacity: 0.9 }}>/ 100</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 4 }}>
                        SCORE DE COLUSIÓN · modo {invResult.mode.toUpperCase()} · {invResult.elapsed_s}s
                      </div>
                      <h3 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
                        {scoreToLabel(invResult.summary.collusion_score)}
                      </h3>
                      <div style={{ marginTop: 4, fontSize: 13 }}>
                        UIDs: {invResult.uids.map((u) => (
                          <code key={u} style={{ background: "hsl(var(--muted))", padding: "1px 6px", margin: "0 2px", borderRadius: 3 }}>{u}</code>
                        ))}
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
                        {invResult.summary.n_signals} señales positivas en{" "}
                        {invResult.summary.detectors_positive.length} detectores
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recomendación */}
              <Card style={{ marginBottom: 12 }}>
                <CardContent style={{ paddingTop: 16, paddingBottom: 16 }}>
                  <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginBottom: 6 }}>
                    🎯 RECOMENDACIÓN OPERACIONAL
                  </div>
                  <div data-testid="inv-recommendation" style={{ fontSize: 14, lineHeight: 1.5 }}>
                    {renderInline(invResult.recommendation)}
                  </div>
                </CardContent>
              </Card>

              {/* Perfiles */}
              {invResult.profiles?.length > 0 && (
                <Card style={{ marginBottom: 12 }}>
                  <CardHeader><CardTitle style={{ fontSize: 14 }}>👤 Perfiles técnicos</CardTitle></CardHeader>
                  <CardContent>
                    <table style={{ width: "100%", fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid hsl(var(--border))", textAlign: "left" }}>
                          <th style={{ padding: 6 }}>UID</th>
                          <th style={{ padding: 6 }}>Nick</th>
                          <th style={{ padding: 6, textAlign: "right" }}>Manos</th>
                          <th style={{ padding: 6, textAlign: "right" }}>VPIP</th>
                          <th style={{ padding: 6, textAlign: "right" }}>PFR</th>
                          <th style={{ padding: 6, textAlign: "right" }}>AF</th>
                          <th style={{ padding: 6, textAlign: "right" }}>BB/100</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invResult.profiles.map((p) => (
                          <tr key={p.uid} data-testid={`profile-row-${p.uid}`}>
                            <td style={{ padding: 6, fontFamily: "monospace" }}>{p.uid}</td>
                            <td style={{ padding: 6 }}>{p.nick || "—"}</td>
                            <td style={{ padding: 6, textAlign: "right" }}>{(p.total_hands || 0).toLocaleString()}</td>
                            <td style={{ padding: 6, textAlign: "right" }}>{p.vpip != null ? `${(p.vpip*100).toFixed(0)}%` : "—"}</td>
                            <td style={{ padding: 6, textAlign: "right" }}>{p.pfr != null ? `${(p.pfr*100).toFixed(0)}%` : "—"}</td>
                            <td style={{ padding: 6, textAlign: "right" }}>{p.aggression_rate != null ? p.aggression_rate.toFixed(2) : "—"}</td>
                            <td style={{
                              padding: 6, textAlign: "right", fontWeight: 600,
                              color: (p.bb_per_100 || 0) > 0 ? "hsl(140 50% 50%)" :
                                     (p.bb_per_100 || 0) < -3 ? "hsl(0 70% 60%)" : "inherit",
                            }}>{p.bb_per_100 != null ? p.bb_per_100.toFixed(2) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Pair summary */}
              {invResult.pair_summary && (
                <Card style={{ marginBottom: 12 }}>
                  <CardHeader><CardTitle style={{ fontSize: 14 }}>👥 Resumen del par</CardTitle></CardHeader>
                  <CardContent>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10, fontSize: 12 }}>
                      <div><div style={{ color: "hsl(var(--muted-foreground))", fontSize: 10 }}>MANOS JUNTOS</div><strong style={{ fontSize: 18 }}>{(invResult.pair_summary.hands_together || 0).toLocaleString()}</strong></div>
                      <div><div style={{ color: "hsl(var(--muted-foreground))", fontSize: 10 }}>FLUJO NETO</div><strong style={{ fontSize: 18 }}>{Math.round(invResult.pair_summary.net_flow_low_to_high || 0).toLocaleString()}</strong></div>
                      <div><div style={{ color: "hsl(var(--muted-foreground))", fontSize: 10 }}>FLUJO BRUTO</div><strong style={{ fontSize: 18 }}>{Math.round(invResult.pair_summary.gross_flow || 0).toLocaleString()}</strong></div>
                      <div><div style={{ color: "hsl(var(--muted-foreground))", fontSize: 10 }}>DOMINANCE</div><strong style={{ fontSize: 18 }}>{(invResult.pair_summary.dominance_ratio || 0).toFixed(3)}</strong></div>
                      <div><div style={{ color: "hsl(var(--muted-foreground))", fontSize: 10 }}>SHOWDOWNS LO/HI</div><strong style={{ fontSize: 18 }}>{invResult.pair_summary.lo_won_count || 0}/{invResult.pair_summary.hi_won_count || 0}</strong></div>
                      <div><div style={{ color: "hsl(var(--muted-foreground))", fontSize: 10 }}>CLUBES · TABLAS</div><strong style={{ fontSize: 18 }}>{(invResult.pair_summary.clubs || []).length}·{(invResult.pair_summary.tables || []).length}</strong></div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Señales detectadas */}
              <Card style={{ marginBottom: 12 }}>
                <CardHeader>
                  <CardTitle style={{ fontSize: 14 }}>🚨 Señales detectadas ({invResult.signals.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {invResult.signals.length === 0 ? (
                    <div style={{ fontSize: 13, color: "hsl(var(--muted-foreground))" }}>
                      Ningún detector produjo señales positivas para estos UIDs.
                    </div>
                  ) : (
                    invResult.signals.map((s, i) => (
                      <details key={i} data-testid={`inv-signal-${s.signal_id}`} style={{ marginBottom: 8, padding: 10, border: "1px solid hsl(var(--border))", borderRadius: 6 }}>
                        <summary style={{ cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <Badge variant={SEV_COLORS[s.severity] || "outline"}>{s.severity}</Badge>
                          <code style={{ fontSize: 11 }}>{s.signal_id}</code>
                          <span style={{ fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
                            conf {s.confidence}% · z {(s.z_score || 0).toFixed(2)}σ · risk {s.risk_score} · {s.decision}
                          </span>
                          <span style={{ marginLeft: "auto", fontSize: 11 }}>
                            <strong>{(s.narrative?.title) || ""}</strong>
                          </span>
                        </summary>
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid hsl(var(--border))", fontSize: 13, lineHeight: 1.6 }}>
                          {renderMarkdownLite(s.narrative?.summary_md || s.evidence?.join("\n") || "(sin narrativa)")}
                          {s.narrative?.action_recommended && (
                            <div style={{ marginTop: 8, padding: 8, fontSize: 12, background: "hsl(45 90% 50% / 0.1)", borderRadius: 4 }}>
                              🎯 <strong>Acción:</strong> {s.narrative.action_recommended}
                            </div>
                          )}
                        </div>
                      </details>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Cómplices candidatos (solo single) */}
              {invResult.suspects_ranking?.length > 0 && (
                <Card style={{ marginBottom: 12 }}>
                  <CardHeader><CardTitle style={{ fontSize: 14 }}>🎭 Cómplices candidatos ({invResult.suspects_ranking.length})</CardTitle></CardHeader>
                  <CardContent>
                    <table style={{ width: "100%", fontSize: 12 }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid hsl(var(--border))", textAlign: "left" }}>
                          <th style={{ padding: 6 }}>UID Cómplice</th>
                          <th style={{ padding: 6, textAlign: "center" }}>Señales</th>
                          <th style={{ padding: 6 }}>Detectores</th>
                          <th style={{ padding: 6 }}>Max severity</th>
                          <th style={{ padding: 6, textAlign: "right" }}>Max conf</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invResult.suspects_ranking.map((r) => (
                          <tr key={r.uid} data-testid={`suspect-row-${r.uid}`}>
                            <td style={{ padding: 6, fontFamily: "monospace" }}>{r.uid}</td>
                            <td style={{ padding: 6, textAlign: "center", fontWeight: 700 }}>{r.signals_count}</td>
                            <td style={{ padding: 6, fontSize: 10 }}>{r.signal_ids.join(", ")}</td>
                            <td style={{ padding: 6 }}><Badge variant={SEV_COLORS[r.max_severity] || "outline"}>{r.max_severity}</Badge></td>
                            <td style={{ padding: 6, textAlign: "right" }}>{r.max_confidence}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              )}

              {/* Top hands */}
              {invResult.top_hands?.length > 0 && (
                <Card style={{ marginBottom: 12 }}>
                  <CardHeader><CardTitle style={{ fontSize: 14 }}>📋 Manos más sospechosas</CardTitle></CardHeader>
                  <CardContent>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {invResult.top_hands.map((h) => (
                        <a key={h.hand_id} href={`/collector-v2/manos?hand_id=${encodeURIComponent(h.hand_id)}`} target="_blank" rel="noreferrer"
                           style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", borderRadius: 4, fontSize: 12, fontFamily: "monospace", textDecoration: "none", color: "hsl(var(--primary))", background: "hsl(var(--muted) / 0.5)" }}>
                          <ExternalLink className="h-3 w-3" />
                          {h.hand_id}
                          <span style={{ marginLeft: "auto", fontFamily: "system-ui", fontSize: 11, color: "hsl(var(--muted-foreground))" }}>
                            {h.n_signals} señal{h.n_signals > 1 ? "es" : ""}
                          </span>
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Drawer / Modal de detalle */}
      {drawerAlert && (
        <div
          data-testid="brain-alert-drawer"
          onClick={closeDrawer}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex", justifyContent: "flex-end",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "min(720px, 100%)", height: "100vh",
              background: "hsl(var(--background))",
              borderLeft: "1px solid hsl(var(--border))",
              overflow: "auto", padding: 24,
              boxShadow: "-8px 0 32px rgba(0,0,0,0.3)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <Badge variant={SEV_COLORS[drawerAlert.severity] || "outline"}>{drawerAlert.severity}</Badge>
                  <span style={{ fontSize: 12, fontFamily: "monospace", color: "hsl(var(--muted-foreground))" }}>
                    {drawerAlert.alert_type} · risk {drawerAlert.risk_score} · conf {drawerAlert.confidence_score}%
                  </span>
                </div>
                <h2 data-testid="drawer-title" style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
                  {drawerAlert.narrative_title || drawerAlert.alert_type}
                </h2>
                <div style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: 4, fontFamily: "monospace" }}>
                  alert_id: {drawerAlert.alert_id}
                </div>
              </div>
              <Button data-testid="drawer-close" variant="ghost" size="icon" onClick={closeDrawer}>
                <XIcon className="h-4 w-4" />
              </Button>
            </div>

            {drawerLoading && <div style={{ padding: 16 }}>Cargando informe técnico…</div>}

            {/* Redacción técnica markdown */}
            <div data-testid="drawer-narrative" style={{
              fontSize: 14, lineHeight: 1.6, marginTop: 16,
              padding: 16, background: "hsl(var(--muted) / 0.3)",
              borderRadius: 8, border: "1px solid hsl(var(--border))",
              fontFamily: "system-ui, -apple-system, sans-serif",
              whiteSpace: "pre-wrap", overflowWrap: "break-word",
            }}>
              {renderMarkdownLite(drawerAlert.narrative_summary_md || drawerAlert.explanation || "(sin narrativa)")}
            </div>

            {/* Acción recomendada */}
            {drawerAlert.narrative_action && (
              <div style={{ marginTop: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>🎯 Acción recomendada</h3>
                <div data-testid="drawer-action" style={{
                  padding: 12, fontSize: 13, lineHeight: 1.5,
                  background: "hsl(45 90% 50% / 0.1)",
                  border: "1px solid hsl(45 90% 50% / 0.3)",
                  borderRadius: 8,
                }}>
                  {drawerAlert.narrative_action}
                </div>
              </div>
            )}

            {/* Tags */}
            {drawerAlert.narrative_tags?.length > 0 && (
              <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {drawerAlert.narrative_tags.map((t) => (
                  <Badge key={t} variant="outline" style={{ fontSize: 11 }}>{t}</Badge>
                ))}
              </div>
            )}

            {/* Sample hand_ids */}
            {drawerAlert.sample_hand_ids?.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>📋 Manos de muestra ({drawerAlert.sample_hand_ids.length})</h3>
                <div data-testid="drawer-hands" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {drawerAlert.sample_hand_ids.slice(0, 10).map((hid) => (
                    <a
                      key={hid}
                      href={`/collector-v2/manos?hand_id=${encodeURIComponent(hid)}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontFamily: "monospace", fontSize: 12,
                        padding: "4px 8px", borderRadius: 4,
                        background: "hsl(var(--muted) / 0.5)",
                        color: "hsl(var(--primary))",
                        textDecoration: "none",
                        display: "inline-flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <ExternalLink className="h-3 w-3" /> {hid}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Evidencia raw técnica */}
            {drawerAlert.evidence && (
              <details style={{ marginTop: 24 }}>
                <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  🔬 Evidencia raw técnica (JSON)
                </summary>
                <pre data-testid="drawer-raw-evidence" style={{
                  marginTop: 8, padding: 12, fontSize: 11,
                  background: "hsl(var(--muted) / 0.5)", borderRadius: 6,
                  overflow: "auto", maxHeight: 300,
                }}>
                  {JSON.stringify(drawerAlert.evidence, null, 2)}
                </pre>
              </details>
            )}

            {/* Decision rationale */}
            {drawerAlert._detail?.alert?.narrative?.decision_rationale && (
              <div style={{ marginTop: 16, fontSize: 12, color: "hsl(var(--muted-foreground))", fontStyle: "italic" }}>
                <strong>Decisión auto:</strong> {drawerAlert._detail.alert.narrative.decision_rationale}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "confirmed-cases" && (
        <ConfirmedCasesPanel />
      )}

      {tab === "linked-devices" && (
        <LinkedDevicesPanel />
      )}
    </div>
  );
}

function scoreToColor(score) {
  if (score >= 80) return "hsl(0 70% 50%)";       // rojo critical
  if (score >= 60) return "hsl(25 90% 55%)";      // naranja
  if (score >= 35) return "hsl(45 90% 50%)";      // amarillo
  if (score > 0)   return "hsl(140 50% 50%)";     // verde
  return "hsl(220 10% 50%)";                       // gris
}

function scoreToLabel(score) {
  if (score >= 80) return "🔴 CRITICAL — Acción inmediata";
  if (score >= 60) return "🟠 HIGH — Revisión priorizada";
  if (score >= 35) return "🟡 MEDIUM — Monitoreo activo";
  if (score > 0)   return "🟢 LOW — Indicios menores";
  return "⚪ CLEAN — Sin señales positivas";
}

/**
 * renderMarkdownLite — render minimalista de un subset de markdown sin dependencias.
 * Soporta: ##, ###, **bold**, `code`, listas con -, bloques de párrafo.
 */
function renderMarkdownLite(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = () => {
    if (listBuf.length > 0) {
      out.push(
        <ul key={`ul-${out.length}`} style={{ paddingLeft: 20, margin: "6px 0" }}>
          {listBuf.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((line, idx) => {
    if (line.startsWith("## ")) {
      flushList();
      out.push(<h2 key={idx} style={{ fontSize: 18, fontWeight: 700, marginTop: 12, marginBottom: 8 }}>{renderInline(line.slice(3))}</h2>);
    } else if (line.startsWith("### ")) {
      flushList();
      out.push(<h3 key={idx} style={{ fontSize: 14, fontWeight: 600, marginTop: 10, marginBottom: 4, color: "hsl(var(--primary))" }}>{renderInline(line.slice(4))}</h3>);
    } else if (line.startsWith("- ")) {
      listBuf.push(line.slice(2));
    } else if (line.startsWith("> ")) {
      flushList();
      out.push(<blockquote key={idx} style={{ borderLeft: "3px solid hsl(var(--primary))", paddingLeft: 10, margin: "6px 0", color: "hsl(var(--muted-foreground))" }}>{renderInline(line.slice(2))}</blockquote>);
    } else if (line.trim() === "") {
      flushList();
      out.push(<div key={idx} style={{ height: 6 }} />);
    } else {
      flushList();
      out.push(<p key={idx} style={{ margin: "4px 0" }}>{renderInline(line)}</p>);
    }
  });
  flushList();
  return out;
}

function renderInline(text) {
  if (!text) return null;
  // Tokenize **bold** y `code`
  const parts = [];
  let buffer = "";
  let i = 0;
  let key = 0;
  while (i < text.length) {
    if (text[i] === "*" && text[i+1] === "*") {
      if (buffer) { parts.push(buffer); buffer = ""; }
      const end = text.indexOf("**", i+2);
      if (end > 0) {
        parts.push(<strong key={`b${key++}`}>{text.slice(i+2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    if (text[i] === "`") {
      if (buffer) { parts.push(buffer); buffer = ""; }
      const end = text.indexOf("`", i+1);
      if (end > 0) {
        parts.push(<code key={`c${key++}`} style={{ background: "hsl(var(--muted))", padding: "1px 5px", borderRadius: 3, fontSize: 12 }}>{text.slice(i+1, end)}</code>);
        i = end + 1;
        continue;
      }
    }
    buffer += text[i];
    i++;
  }
  if (buffer) parts.push(buffer);
  return parts;
}

// ─────────────────────────────────────────────────────────────────────
// Panel: Casos Confirmados (aprendizaje supervisado del Brain)
// ─────────────────────────────────────────────────────────────────────
function ConfirmedCasesPanel() {
  const [cases, setCases] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [diagnosing, setDiagnosing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ uids: "", label: "", liga_id: "", club_name: "", mesas: "", notes: "" });
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/v2/brain/confirmed-cases");
      const j = await r.json();
      setCases(j.cases || []);
      setHealth(j.health || null);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const diagnose = async (case_id) => {
    setDiagnosing(case_id);
    try {
      await fetch(`/api/v2/brain/confirmed-cases/${case_id}/diagnose`, { method: "POST" });
      await load();
    } catch (e) { setErr(e.message); }
    finally { setDiagnosing(null); }
  };

  const removeCase = async (case_id) => {
    if (!window.confirm("¿Borrar este caso confirmado?")) return;
    await fetch(`/api/v2/brain/confirmed-cases/${case_id}`, { method: "DELETE" });
    await load();
  };

  const submit = async () => {
    const uids = form.uids.split(/[,\s]+/).map(x => parseInt(x, 10)).filter(x => x > 0);
    if (uids.length < 1) { setErr("Ingresá al menos un UID"); return; }
    const evidence = {};
    if (form.liga_id) evidence.liga_id = parseInt(form.liga_id, 10);
    if (form.club_name) evidence.club_name = form.club_name;
    if (form.mesas) evidence.mesas_confirmadas = form.mesas.split(/[,\s]+/).filter(x => x);
    const body = {
      uids, label: form.label || `Caso ${uids.join("+")}`,
      mode: uids.length === 2 ? "pair" : uids.length === 1 ? "single" : "group",
      evidence, notes: form.notes, source: "user_manual",
    };
    const r = await fetch("/api/v2/brain/confirmed-cases", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const j = await r.json();
    if (j.ok) {
      setShowForm(false);
      setForm({ uids: "", label: "", liga_id: "", club_name: "", mesas: "", notes: "" });
      await load();
    } else { setErr(j.error || "falló"); }
  };

  const verdictBadge = (v) => {
    const map = {
      caught: { color: "#10b981", text: "✅ Detectado" },
      missed: { color: "#ef4444", text: "❌ Falló" },
      data_gap: { color: "#f59e0b", text: "📭 Faltan datos" },
      weak_signal: { color: "#eab308", text: "🟡 Señal débil" },
      pending: { color: "#94a3b8", text: "⏳ Sin diagnosticar" },
    };
    const cfg = map[v] || map.pending;
    return <span style={{ background: cfg.color, color: "white", padding: "3px 10px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{cfg.text}</span>;
  };

  return (
    <div data-testid="confirmed-cases-tab">
      {health && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <StatCard label="Total" value={health.total_cases} />
          <StatCard label="Detectados" value={health.counts?.caught || 0} color="#10b981" />
          <StatCard label="Falló" value={health.counts?.missed || 0} color="#ef4444" />
          <StatCard label="Faltan datos" value={health.counts?.data_gap || 0} color="#f59e0b" />
          <StatCard label="Catch rate" value={`${health.catch_rate_pct}%`} color="hsl(var(--primary))" />
        </div>
      )}

      <div style={{ marginBottom: 16, display: "flex", gap: 8 }}>
        <Button onClick={() => setShowForm(!showForm)} data-testid="btn-add-case">
          {showForm ? "Cancelar" : "➕ Marcar nuevo caso confirmado"}
        </Button>
        <Button onClick={load} variant="outline" disabled={loading}>🔄 Refresh</Button>
      </div>

      {showForm && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader><CardTitle style={{ fontSize: 14 }}>Nuevo Caso Confirmado</CardTitle></CardHeader>
          <CardContent style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>UIDs (separados por coma o espacio):</label>
              <input value={form.uids} onChange={e => setForm({ ...form, uids: e.target.value })}
                placeholder="13510957, 13510980" style={confirmedInputStyle} data-testid="input-uids" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Etiqueta del caso:</label>
              <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                placeholder="Flow United Liga 2006 - 2 cuentas colusión" style={confirmedInputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Liga ID:</label>
              <input value={form.liga_id} onChange={e => setForm({ ...form, liga_id: e.target.value })}
                placeholder="2006" style={confirmedInputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Club:</label>
              <input value={form.club_name} onChange={e => setForm({ ...form, club_name: e.target.value })}
                placeholder="Flow United" style={confirmedInputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Mesas confirmadas (game_ids separados por coma):</label>
              <input value={form.mesas} onChange={e => setForm({ ...form, mesas: e.target.value })}
                placeholder="260504202822-7018884, 260504203251-7020591" style={confirmedInputStyle} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Notas:</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                rows={2} style={{ ...confirmedInputStyle, width: "100%", fontFamily: "inherit" }} />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <Button onClick={submit} data-testid="btn-submit-case">💾 Guardar caso</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {err && <div style={{ background: "#fee2e2", color: "#991b1b", padding: 10, borderRadius: 6, marginBottom: 12 }}>{err}</div>}

      <Card>
        <CardContent style={{ paddingTop: 16 }}>
          {loading && <div>Cargando…</div>}
          {!loading && cases.length === 0 && <div style={{ color: "#666" }}>Sin casos registrados aún.</div>}
          {!loading && cases.map(c => {
            const d = c.last_diagnosis;
            const verdict = d?.verdict || "pending";
            return (
              <div key={c.case_id} data-testid={`case-${c.case_id}`}
                style={{ borderTop: "1px solid hsl(var(--border))", padding: "14px 0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
                      <strong style={{ fontSize: 14 }}>{c.label}</strong>
                      {verdictBadge(verdict)}
                    </div>
                    <div style={{ fontSize: 12, color: "#888" }}>
                      UIDs: <code>{c.uids.join(", ")}</code> · modo: <code>{c.mode}</code> · {c.source}
                    </div>
                    {c.evidence?.club_name && (
                      <div style={{ fontSize: 12, color: "#888", marginTop: 3 }}>
                        Club: <strong>{c.evidence.club_name}</strong> · Liga: <strong>{c.evidence.liga_id || "?"}</strong>
                        {c.evidence.mesas_confirmadas?.length > 0 && (
                          <span> · {c.evidence.mesas_confirmadas.length} mesa(s) confirmada(s)</span>
                        )}
                      </div>
                    )}
                    {c.notes && <div style={{ fontSize: 12, fontStyle: "italic", color: "#999", marginTop: 4 }}>{c.notes}</div>}
                    {d && (
                      <div style={{ background: "hsl(var(--muted))", padding: 10, borderRadius: 6, marginTop: 8, fontSize: 12 }}>
                        <div style={{ marginBottom: 6 }}>
                          <strong>{d.verdict_emoji} {d.verdict_msg}</strong>
                        </div>
                        <div>Score Brain: <strong>{d.score}/100</strong> · Señales: <strong>{d.n_signals_for_target}</strong>
                          {d.hands_together !== null && d.hands_together !== undefined && (<> · Manos juntas: <strong>{d.hands_together}</strong></>)}
                        </div>
                        {d.data_gaps?.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>📭 Gaps de datos:</strong>
                            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                              {d.data_gaps.map((g, i) => (
                                <li key={i} style={{ color: g.severity === "critical" ? "#ef4444" : "#f59e0b" }}>{g.msg}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {d.suggestions?.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <strong>💡 Sugerencias:</strong>
                            <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                              {d.suggestions.map((sg, i) => <li key={i}>{sg}</li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <Button size="sm" onClick={() => diagnose(c.case_id)}
                      disabled={diagnosing === c.case_id} data-testid={`btn-diagnose-${c.case_id}`}>
                      {diagnosing === c.case_id ? "Analizando…" : (d ? "🔄 Re-diagnosticar" : "🔍 Diagnosticar")}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => removeCase(c.case_id)}>🗑️</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <Card style={{ minWidth: 120, flex: "0 1 auto" }}>
      <CardContent style={{ paddingTop: 14, paddingBottom: 14 }}>
        <div style={{ fontSize: 10, color: "#888", textTransform: "uppercase" }}>{label}</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: color || "hsl(var(--primary))" }}>{value}</div>
      </CardContent>
    </Card>
  );
}

const confirmedInputStyle = {
  marginTop: 4, padding: "6px 10px", borderRadius: 4,
  border: "1px solid hsl(var(--border))", background: "hsl(var(--background))",
  fontSize: 13, width: "100%", boxSizing: "border-box",
};

// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// Panel: Dispositivos Vinculados (PPPoker Associate Accounts)
// Vista AGRUPADA por (Union ID, Club ID) — club con mayor profit arriba.
// ─────────────────────────────────────────────────────────────────────
function LinkedDevicesPanel() {
  const [groups, setGroups] = useState([]);
  const [health, setHealth] = useState(null);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [verdictFilter, setVerdictFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [err, setErr] = useState(null);
  // Informe Forense modal
  const [forensicReport, setForensicReport] = useState(null);
  const [forensicMenuPair, setForensicMenuPair] = useState(null);
  // 🔍 Filtros forenses globales (FASE collusion)
  const [coPlayedOnly, setCoPlayedOnly] = useState(false);
  const [onlyWithCollusion, setOnlyWithCollusion] = useState(false);
  const [resultFilter, setResultFilter] = useState("");  // "" | "win" | "loss"
  const [thisWeekOnly, setThisWeekOnly] = useState(false);
  const [weekStartAt, setWeekStartAt] = useState(null);
  const [weekLabel, setWeekLabel] = useState("");
  // 🔁 Polling silencioso: snapshot del contador hands_since al cargar grupos
  // + flag de actualización pendiente cuando el collector ingiere nuevas manos.
  const [loadedHandsSince, setLoadedHandsSince] = useState(null);
  const [pendingHands, setPendingHands] = useState(0);

  // Cargar week counter del collector (semana en curso del lado server)
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await fetch("/api/v2/collector/week-counter");
        if (!r.ok) return;
        const j = await r.json();
        if (cancel) return;
        setWeekStartAt(j.week_start_at || null);
        setWeekLabel(j.label || "");
      } catch (_) {}
    })();
    return () => { cancel = true; };
  }, []);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (verdictFilter) params.set("verdict", verdictFilter);
      if (coPlayedOnly) params.set("co_played_only", "true");
      if (onlyWithCollusion) params.set("only_with_collusion", "true");
      if (resultFilter) params.set("result_filter", resultFilter);
      if (thisWeekOnly && weekStartAt) params.set("since", weekStartAt);
      const query = params.toString() ? `?${params.toString()}` : "";
      const flatParams = new URLSearchParams();
      if (verdictFilter) flatParams.set("verdict", verdictFilter);
      flatParams.set("limit", "1");
      const [rGrp, rFlat] = await Promise.all([
        fetch(`/api/v2/brain/linked-devices/by-club${query}`),
        fetch(`/api/v2/brain/linked-devices?${flatParams.toString()}`),
      ]);
      const jGrp = await rGrp.json();
      const jFlat = await rFlat.json();
      setGroups(jGrp.groups || []);
      setHealth(jFlat.health || null);
      setImports(jFlat.imports || []);
      // Snapshot del contador para detectar inserciones nuevas vía polling
      try {
        const rWk = await fetch("/api/v2/collector/week-counter");
        if (rWk.ok) {
          const jWk = await rWk.json();
          setLoadedHandsSince(typeof jWk.hands_since === "number" ? jWk.hands_since : null);
          setPendingHands(0);
        }
      } catch (_) {}
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [verdictFilter, coPlayedOnly, onlyWithCollusion, resultFilter, thisWeekOnly, weekStartAt]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // 🔁 Polling silencioso cada 30s: detectar manos nuevas ingeridas por el collector
  useEffect(() => {
    if (loadedHandsSince == null) return;
    const id = setInterval(async () => {
      try {
        const r = await fetch("/api/v2/collector/week-counter");
        if (!r.ok) return;
        const j = await r.json();
        if (typeof j.hands_since === "number") {
          const diff = j.hands_since - loadedHandsSince;
          if (diff > 0) setPendingHands(diff);
        }
      } catch (_) {}
    }, 30000);
    return () => clearInterval(id);
  }, [loadedHandsSince]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch("/api/v2/brain/linked-devices/import", { method: "POST", body: fd });
      const j = await r.json();
      if (j.ok) {
        alert(`✅ Importado: ${j.rows_processed} filas, ${j.new_pairs} pares nuevos, ${j.hub_uids?.length || 0} hubs.`);
        await loadGroups();
      } else { setErr(j.error || "import falló"); }
    } catch (e) { setErr(e.message); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const crossCheck = async (pair_key) => {
    setAnalyzing(pair_key);
    try {
      await fetch(`/api/v2/brain/linked-devices/${pair_key}/cross-check`, { method: "POST" });
      await loadGroups();
    } finally { setAnalyzing(null); }
  };

  const analyze = async (pair_key) => {
    setAnalyzing(pair_key);
    try {
      const r = await fetch(`/api/v2/brain/linked-devices/${pair_key}/analyze`, { method: "POST" });
      const j = await r.json();
      const t = j.technical || {};
      alert(`🔬 Análisis técnico del par ${pair_key}:\nScore: ${t.score}/100\nSeñales: ${t.signals_count}\nManos juntas: ${t.hands_together}\nDetectores que dispararon:\n${(t.fired_detectors || []).join("\n") || "(ninguno)"}`);
      await loadGroups();
    } finally { setAnalyzing(null); }
  };

  // Abrir informe forense para un par + semana
  // También carga las alertas narrativas del Brain que involucran a ese par (mismo
  // estilo de detalle que el drawer del tab Dashboard)
  const openForensic = async (pair_key, week) => {
    setForensicMenuPair(null);
    setForensicReport({ pair_key, week, loading: true, alerts_loading: true });
    try {
      const r = await fetch(`/api/v2/brain/linked-devices/${pair_key}/forensic-report?week=${week}`);
      const j = await r.json();
      // Extraer uids del par para buscar alerts
      const uid_a = j?.uid_a;
      const uid_b = j?.uid_b;
      let alerts_items = [];
      if (uid_a && uid_b) {
        try {
          const aRes = await fetch(`/api/v2/brain/alerts?uids=${uid_a},${uid_b}&limit=50`);
          const aJ = await aRes.json();
          alerts_items = aJ?.items || [];
        } catch { /* sin alerts es ok */ }
      }
      setForensicReport({ pair_key, week, data: j, loading: false, alerts: alerts_items, alerts_loading: false });
    } catch (e) {
      setForensicReport({ pair_key, week, error: e.message, loading: false, alerts_loading: false });
    }
  };

  const verdictBadge = (v) => {
    const map = {
      collusion_suspected: { color: "#ef4444", text: "🔴 COLLUSION" },
      bot_suspected: { color: "#f97316", text: "🟠 BOT" },
      legitimate_multiaccount: { color: "#22c55e", text: "🟢 LEGÍTIMO" },
      data_gap: { color: "#94a3b8", text: "📭 Sin datos" },
      weak_evidence: { color: "#eab308", text: "🟡 Débil" },
      pending: { color: "#64748b", text: "⏳ Pending" },
    };
    const cfg = map[v] || map.pending;
    return <span style={{ background: cfg.color, color: "white", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>{cfg.text}</span>;
  };

  const fmtProfit = (n) => {
    const v = Number(n || 0);
    const sign = v > 0 ? "+" : v < 0 ? "−" : "";
    const abs = Math.abs(v).toLocaleString("es-AR");
    return `${sign}${abs}`;
  };

  // Filtrar grupos: por UID dentro de los pares y/o por club_id/union_id
  const visibleGroups = useMemo(() => {
    if (!filter) return groups;
    const f = filter.trim().toLowerCase();
    return groups
      .map(g => {
        const headerMatch =
          String(g.club_id || "").includes(f) ||
          String(g.league_id || "").includes(f) ||
          (g.club_name || "").toLowerCase().includes(f);
        const filteredPairs = headerMatch
          ? g.pairs
          : g.pairs.filter(p =>
              p.pair_key.includes(f) ||
              String(p.uid_a).includes(f) ||
              String(p.uid_b).includes(f)
            );
        if (!headerMatch && filteredPairs.length === 0) return null;
        return { ...g, pairs: filteredPairs };
      })
      .filter(Boolean);
  }, [groups, filter]);

  const totalPairsAcrossGroups = useMemo(
    () => visibleGroups.reduce((acc, g) => acc + (g.pairs?.length || 0), 0),
    [visibleGroups]
  );

  const toggleGroup = (key) => setExpanded(x => ({ ...x, [key]: !x[key] }));

  return (
    <div data-testid="linked-devices-tab">
      {health && (
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <StatCard label="Total pares" value={health.total_pairs} />
          <StatCard label="🔴 Collusion" value={health.counts?.collusion_suspected || 0} color="#ef4444" />
          <StatCard label="🟠 Bot" value={health.counts?.bot_suspected || 0} color="#f97316" />
          <StatCard label="🟢 Legítimo" value={health.counts?.legitimate_multiaccount || 0} color="#22c55e" />
          <StatCard label="📭 Sin data" value={health.counts?.data_gap || 0} />
          <StatCard label="🏭 Hubs" value={health.hubs_count || 0} color="#9333ea" />
          <StatCard label="🏛️ Grupos (Union/Club)" value={groups.length} color="#0ea5e9" />
        </div>
      )}

      <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ background: "hsl(var(--primary))", color: "#000", padding: "8px 14px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
          {uploading ? "⏳ Subiendo..." : "📤 Importar CSV PPPoker"}
          <input type="file" accept=".csv" onChange={handleUpload} disabled={uploading} style={{ display: "none" }} data-testid="input-csv-upload" />
        </label>
        {imports && imports.length > 0 && (() => {
          const impactful = imports.filter(im => (im.new_pairs > 0) || (im.new_set_rows > 0));
          if (impactful.length === 0) return null;
          return (
            <details data-testid="imports-history-dropdown" style={{ position: "relative", fontSize: 12 }}>
              <summary style={{ cursor: "pointer", listStyle: "none", background: "#f1f5f9", color: "#0f172a", padding: "8px 12px", borderRadius: 6, fontWeight: 600, border: "1px solid #cbd5e1", display: "inline-flex", alignItems: "center", gap: 6 }}>
                📚 Importados ({impactful.length})
                <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
              </summary>
              <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", padding: 10, minWidth: 380, maxHeight: 360, overflowY: "auto", zIndex: 50 }}>
                <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: "#64748b", marginBottom: 6, fontWeight: 700 }}>
                  Historial de CSVs con impacto
                </div>
                {impactful.map((im) => {
                  const dt = im.imported_at ? new Date(im.imported_at) : null;
                  const fechaCorta = dt ? dt.toLocaleString("es-AR", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  }) : "—";
                  return (
                    <div key={im.import_id} style={{ padding: "6px 4px", borderBottom: "1px solid #f1f5f9", display: "flex", flexDirection: "column", gap: 2 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontWeight: 600, color: "#0f172a", fontSize: 12, wordBreak: "break-all" }}>
                          📄 {im.filename || "upload.csv"}
                        </span>
                        <span style={{ fontSize: 10, color: "#64748b", whiteSpace: "nowrap", fontFamily: "monospace" }}>
                          {fechaCorta}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 8, fontSize: 10, color: "#475569", flexWrap: "wrap" }}>
                        {im.new_pairs > 0 && (
                          <span style={{ background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>
                            +{im.new_pairs} pares
                          </span>
                        )}
                        {im.new_set_rows > 0 && (
                          <span style={{ background: "#dbeafe", color: "#1e40af", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>
                            +{im.new_set_rows} setids
                          </span>
                        )}
                        <span style={{ opacity: 0.7 }}>
                          {im.rows_processed} filas · {im.total_pairs} pares total
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          );
        })()}
        <select value={verdictFilter} onChange={e => setVerdictFilter(e.target.value)} style={confirmedInputStyle} data-testid="select-verdict-filter">
          <option value="">Todos los veredictos</option>
          <option value="collusion_suspected">🔴 Collusion</option>
          <option value="bot_suspected">🟠 Bot</option>
          <option value="legitimate_multiaccount">🟢 Legítimo</option>
          <option value="data_gap">📭 Sin data</option>
        </select>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filtrar por UID, Union, Club o nombre…"
          style={{ ...confirmedInputStyle, width: 280 }}
          data-testid="input-filter"
        />
        <Button onClick={loadGroups} variant="outline" disabled={loading} data-testid="btn-refresh-groups">🔄 Refresh</Button>
        <span style={{ fontSize: 11, color: "#888", marginLeft: 8 }}>
          {visibleGroups.length} grupos · {totalPairsAcrossGroups} pares
        </span>
        {pendingHands > 0 && (
          <button
            type="button"
            onClick={loadGroups}
            data-testid="badge-pending-hands"
            title={`El collector ingirió ${pendingHands.toLocaleString("es-AR")} manos nuevas desde la última carga. Click para refrescar.`}
            style={{
              marginLeft: 10, padding: "3px 10px", borderRadius: 999,
              border: "1px solid #f59e0b", background: "#fef3c7",
              color: "#92400e", fontSize: 11, fontWeight: 600, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            🆕 +{pendingHands.toLocaleString("es-AR")} manos nuevas · Refrescá
          </button>
        )}
      </div>

      {/* 🔍 Filtros forenses globales */}
      <div style={{
        marginBottom: 16, padding: "10px 12px", background: "hsl(var(--muted))",
        borderRadius: 8, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
        fontSize: 12,
      }}>
        <span style={{ fontWeight: 700, color: "#374151" }}>🔍 Filtros forenses:</span>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={onlyWithCollusion}
            onChange={e => setOnlyWithCollusion(e.target.checked)}
            data-testid="cb-only-collusion"
          />
          <span>Solo grupos con 2+ UIDs <strong style={{ color: "#dc2626" }}>collusion</strong></span>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={coPlayedOnly}
            onChange={e => setCoPlayedOnly(e.target.checked)}
            data-testid="cb-co-played-only"
          />
          <span>Solo grupos con <strong>manos co-jugadas</strong> reales</span>
        </label>
        <div style={{ display: "flex", gap: 4, alignItems: "center", marginLeft: 12 }}>
          <span style={{ color: "#64748b" }}>Resultado vs terceros:</span>
          <button
            type="button"
            onClick={() => setResultFilter(resultFilter === "win" ? "" : "win")}
            data-testid="btn-result-win"
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              cursor: "pointer", border: "1px solid #16a34a",
              background: resultFilter === "win" ? "#16a34a" : "transparent",
              color: resultFilter === "win" ? "#fff" : "#16a34a",
            }}
          >🟢 Solo Ganadores</button>
          <button
            type="button"
            onClick={() => setResultFilter(resultFilter === "loss" ? "" : "loss")}
            data-testid="btn-result-loss"
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              cursor: "pointer", border: "1px solid #dc2626",
              background: resultFilter === "loss" ? "#dc2626" : "transparent",
              color: resultFilter === "loss" ? "#fff" : "#dc2626",
            }}
          >🔴 Solo Perdedores</button>

          <span style={{ color: "#cbd5e1", margin: "0 4px" }}>|</span>
          <span style={{ color: "#64748b" }}>Período:</span>
          <button
            type="button"
            onClick={() => setThisWeekOnly(v => !v)}
            disabled={!weekStartAt}
            data-testid="btn-this-week"
            title={weekStartAt
              ? `Filtrar manos co-jugadas desde ${new Date(weekStartAt).toLocaleString("es-AR")} (${weekLabel})`
              : "Esperando week-counter del collector..."}
            style={{
              padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
              cursor: weekStartAt ? "pointer" : "not-allowed",
              border: "1px solid #2563eb",
              background: thisWeekOnly ? "#2563eb" : "transparent",
              color: thisWeekOnly ? "#fff" : "#2563eb",
              opacity: weekStartAt ? 1 : 0.5,
            }}
          >📅 Esta semana{weekLabel ? ` (${weekLabel})` : ""}</button>
        </div>
        {(coPlayedOnly || onlyWithCollusion || resultFilter || thisWeekOnly) && (
          <button
            type="button"
            onClick={() => { setCoPlayedOnly(false); setOnlyWithCollusion(false); setResultFilter(""); setThisWeekOnly(false); }}
            style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, border: "1px solid #94a3b8", background: "transparent", color: "#64748b", cursor: "pointer" }}
            data-testid="btn-clear-forensic-filters"
          >Limpiar filtros forenses</button>
        )}
      </div>

      {err && <div style={{ background: "#fee2e2", color: "#991b1b", padding: 10, borderRadius: 6, marginBottom: 12 }}>{err}</div>}

      {loading && <div>Cargando…</div>}
      {!loading && visibleGroups.length === 0 && <div style={{ color: "#666" }}>Sin grupos. Subí un CSV.</div>}

      {!loading && visibleGroups.map(g => {
        const key = `${g.league_id || 0}-${g.club_id || 0}`;
        const isOpen = expanded[key] ?? false;
        const profit = Number(g.total_profit_raw || 0);
        const profitColor = profit > 0 ? "#22c55e" : profit < 0 ? "#ef4444" : "#94a3b8";
        const profitIcon = profit > 0 ? "📈" : profit < 0 ? "📉" : "·";
        return (
          <Card key={key} style={{ marginBottom: 10 }} data-testid={`group-${key}`}>
            <CardContent style={{ paddingTop: 14, paddingBottom: 14 }}>
              {/* Header del grupo */}
              <div
                onClick={() => toggleGroup(key)}
                style={{ display: "flex", alignItems: "center", gap: 14, cursor: "pointer", flexWrap: "wrap" }}
                data-testid={`group-header-${key}`}
              >
                <div style={{ fontSize: 18, color: "#888", width: 18 }}>{isOpen ? "▾" : "▸"}</div>
                <div style={{ flex: 1, minWidth: 260 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Union</span>
                    <strong style={{ fontFamily: "monospace", fontSize: 13 }}>{g.league_id ?? "—"}</strong>
                    {g.union_name && <span style={{ fontSize: 12, color: "#475569" }}>· {g.union_name}</span>}
                    <span style={{ color: "#cbd5e1" }}>|</span>
                    <span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Club</span>
                    <strong style={{ fontFamily: "monospace", fontSize: 13 }}>{g.club_id ?? "—"}</strong>
                    <span style={{ fontSize: 14, fontWeight: 600 }}>· {g.club_name || <em style={{ color: "#94a3b8" }}>sin nombre</em>}</span>
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                    {g.n_pairs} pares · {g.total_hands} manos en BD · {g.setids_count} setids reportadas
                    {g.verdict_counts && Object.keys(g.verdict_counts).length > 0 && (
                      <span style={{ marginLeft: 8 }}>
                        {Object.entries(g.verdict_counts).map(([v, n]) => (
                          <span key={v} style={{ marginRight: 6 }}>{verdictBadge(v)} <strong>{n}</strong></span>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ textAlign: "right", display: "flex", gap: 16, alignItems: "flex-start" }}>
                  {/* Métricas de colusión efectiva */}
                  {(g.collusion_metrics?.co_played_count > 0) && (() => {
                    const cm = g.collusion_metrics;
                    const vs = Number(cm.co_profit_vs_others || 0);
                    const vsColor = vs > 0 ? "#16a34a" : vs < 0 ? "#dc2626" : "#94a3b8";
                    const vsIcon = vs > 0 ? "🟢" : vs < 0 ? "🔴" : "•";
                    return (
                      <div style={{
                        textAlign: "right", borderRight: "1px solid hsl(var(--border))",
                        paddingRight: 16,
                      }} data-testid={`group-collusion-${key}`}>
                        <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Profit colusión vs terceros
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: vsColor, fontFamily: "monospace" }}>
                          {vsIcon} {fmtProfit(vs)}
                        </div>
                        <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>
                          {cm.co_played_count} manos co-jugadas · {cm.collusion_uids_count} UIDs collusion
                        </div>
                        <div style={{ fontSize: 10, marginTop: 2 }}>
                          <span style={{ color: "#16a34a" }}>🟢 {cm.co_winning_hands}w {fmtProfit(cm.co_profit_winning)}</span>
                          {" · "}
                          <span style={{ color: "#dc2626" }}>🔴 {cm.co_losing_hands}L {fmtProfit(cm.co_profit_losing)}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>Profit total (raw)</div>
                    <div style={{ fontSize: 18, fontWeight: 700, color: profitColor, fontFamily: "monospace" }} data-testid={`group-profit-${key}`}>
                      {profitIcon} {fmtProfit(profit)}
                    </div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>(todas las manos del club)</div>
                  </div>
                </div>
              </div>

              {/* Lista de pares (colapsable) */}
              {isOpen && (
                <div style={{ marginTop: 12, borderTop: "1px solid hsl(var(--border))", paddingTop: 10 }}>
                  {(g.pairs || []).length === 0 && <div style={{ color: "#888", fontSize: 12 }}>Sin pares visibles.</div>}
                  {(g.pairs || []).map(p => {
                    const cc = p.cross_check || {};
                    const tech = p.technical || {};
                    const pp = Number(p.profit_raw_in_club || 0);
                    const ppColor = pp > 0 ? "#16a34a" : pp < 0 ? "#dc2626" : "#94a3b8";
                    return (
                      <div key={p.pair_key} data-testid={`pair-${p.pair_key}`}
                        style={{ borderTop: "1px dashed hsl(var(--border))", padding: "10px 0" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ flex: 1, minWidth: 320 }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
                              <strong style={{ fontSize: 14, fontFamily: "monospace" }}>{p.uid_a} ⟷ {p.uid_b}</strong>
                              {verdictBadge(p.verdict)}
                              <span style={{ fontSize: 11, color: "#888" }}>· {p.setids_count} mesas reportadas</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: ppColor, fontFamily: "monospace" }} data-testid={`pair-profit-${p.pair_key}`}>
                                💰 Par en este club: {fmtProfit(pp)}
                              </span>
                              {p.hands_in_club > 0 && (
                                <span style={{ fontSize: 11, color: "#475569" }}>· {p.hands_in_club} manos en este club</span>
                              )}
                            </div>

                            {/* Breakdown por UID individual */}
                            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", background: "hsl(var(--muted))", padding: "6px 10px", borderRadius: 6, fontSize: 11, marginBottom: 6, marginTop: 2 }}>
                              <div data-testid={`uid-block-${p.uid_a}`}>
                                <span style={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>UID A · </span>
                                <strong style={{ fontFamily: "monospace" }}>{p.uid_a}</strong>
                                <span style={{ margin: "0 8px", color: "#94a3b8" }}>|</span>
                                <span>En este club: <strong style={{ color: (p.profit_uid_a||0) > 0 ? "#16a34a" : (p.profit_uid_a||0) < 0 ? "#dc2626" : "#64748b", fontFamily: "monospace" }}>{fmtProfit(p.profit_uid_a)}</strong></span>
                                <span style={{ marginLeft: 6, color: "#94a3b8" }}>({p.hands_uid_a_in_club || 0} manos)</span>
                                <span style={{ margin: "0 8px", color: "#94a3b8" }}>·</span>
                                <span>Global: <strong style={{ color: (p.profit_global_uid_a||0) > 0 ? "#16a34a" : (p.profit_global_uid_a||0) < 0 ? "#dc2626" : "#64748b", fontFamily: "monospace" }}>{fmtProfit(p.profit_global_uid_a)}</strong></span>
                                <span style={{ marginLeft: 6, color: "#94a3b8" }}>({p.hands_global_uid_a || 0} manos)</span>
                              </div>
                              <div data-testid={`uid-block-${p.uid_b}`}>
                                <span style={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4, fontSize: 10 }}>UID B · </span>
                                <strong style={{ fontFamily: "monospace" }}>{p.uid_b}</strong>
                                <span style={{ margin: "0 8px", color: "#94a3b8" }}>|</span>
                                <span>En este club: <strong style={{ color: (p.profit_uid_b||0) > 0 ? "#16a34a" : (p.profit_uid_b||0) < 0 ? "#dc2626" : "#64748b", fontFamily: "monospace" }}>{fmtProfit(p.profit_uid_b)}</strong></span>
                                <span style={{ marginLeft: 6, color: "#94a3b8" }}>({p.hands_uid_b_in_club || 0} manos)</span>
                                <span style={{ margin: "0 8px", color: "#94a3b8" }}>·</span>
                                <span>Global: <strong style={{ color: (p.profit_global_uid_b||0) > 0 ? "#16a34a" : (p.profit_global_uid_b||0) < 0 ? "#dc2626" : "#64748b", fontFamily: "monospace" }}>{fmtProfit(p.profit_global_uid_b)}</strong></span>
                                <span style={{ marginLeft: 6, color: "#94a3b8" }}>({p.hands_global_uid_b || 0} manos)</span>
                              </div>
                            </div>

                            <div style={{ fontSize: 11, color: "#888", display: "flex", gap: 14, flexWrap: "wrap" }}>
                              <span style={{ color: cc.hands_together_count > 0 ? "#ef4444" : "inherit" }}>
                                Manos JUNTAS: <strong>{cc.hands_together_count ?? 0}</strong>
                              </span>
                              {typeof p.weekly_hands_together === "number" && p.weekly_hands_together > 0 && (
                                <span style={{ background: "#dcfce7", color: "#166534", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }} title="Manos juntas en la semana filtrada">
                                  📅 esta semana: {p.weekly_hands_together}
                                </span>
                              )}
                              {typeof p.recent_co_hands_24h === "number" && p.recent_co_hands_24h > 0 && (
                                <span style={{ background: "#fee2e2", color: "#991b1b", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }} title={`Manos juntas en últimas 24h${p.last_co_played_at ? ` · última: ${new Date(p.last_co_played_at).toLocaleString()}` : ""}`}>
                                  🔥 24h: {p.recent_co_hands_24h}
                                </span>
                              )}
                              {typeof p.recent_co_hands_7d === "number" && p.recent_co_hands_7d > 0 && (
                                <span style={{ background: "#fef3c7", color: "#92400e", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }} title="Manos juntas en últimos 7 días (auto-refresh cada 10 min)">
                                  📊 7d: {p.recent_co_hands_7d}
                                </span>
                              )}
                              <span style={{ color: cc.concurrent_diff_table_count > 5 ? "#f97316" : "inherit" }}>
                                Concurrentes (otras mesas): <strong>{cc.concurrent_diff_table_count ?? 0}</strong>
                              </span>
                              <span>Mesas en BD: <strong>{cc.tables_in_db_count ?? 0}/{cc.tables_reported_count ?? 0}</strong></span>
                            </div>
                            {(p.leagueids?.length > 0 || p.clubids?.length > 0) && (
                              <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                                Ligas: {(p.leagueids || []).join(", ") || "-"} · Clubs: {(p.clubids || []).join(", ") || "-"}
                              </div>
                            )}
                            {tech.score !== undefined && (
                              <div style={{ background: "hsl(var(--muted))", padding: 8, borderRadius: 6, marginTop: 6, fontSize: 11 }}>
                                🔬 Brain technical: score=<strong>{tech.score}/100</strong> · señales=<strong>{tech.signals_count}</strong>
                                {tech.fired_detectors?.length > 0 && (
                                  <div style={{ marginTop: 4 }}>Detectores: {tech.fired_detectors.join(", ")}</div>
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 4, position: "relative" }}>
                            <Button size="sm" onClick={() => crossCheck(p.pair_key)} disabled={analyzing === p.pair_key} data-testid={`btn-crosscheck-${p.pair_key}`}>
                              {analyzing === p.pair_key ? "..." : "🔄 Cross-check"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setForensicMenuPair(forensicMenuPair === p.pair_key ? null : p.pair_key)}
                              data-testid={`btn-forensic-${p.pair_key}`}
                            >
                              📋 Informe Forense ▾
                            </Button>
                            {forensicMenuPair === p.pair_key && (
                              <div
                                style={{
                                  position: "absolute", top: 64, right: 0, zIndex: 30,
                                  background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
                                  borderRadius: 8, padding: 6, minWidth: 220,
                                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)"
                                }}
                                data-testid={`forensic-menu-${p.pair_key}`}
                              >
                                <button
                                  type="button"
                                  onClick={() => openForensic(p.pair_key, "current")}
                                  style={menuBtn}
                                  data-testid={`forensic-current-${p.pair_key}`}
                                >📅 Esta semana (Lun → Dom)</button>
                                <button
                                  type="button"
                                  onClick={() => openForensic(p.pair_key, "previous")}
                                  style={menuBtn}
                                  data-testid={`forensic-previous-${p.pair_key}`}
                                >📆 Semana pasada (Lun → Dom)</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Modal del informe forense */}
      {forensicReport && (
        <ForensicReportModal
          report={forensicReport}
          onClose={() => setForensicReport(null)}
        />
      )}
    </div>
  );
}

// Estilo para items del menú desplegable del informe forense
const menuBtn = {
  display: "block", width: "100%", textAlign: "left",
  padding: "8px 12px", border: "none", background: "transparent",
  cursor: "pointer", fontSize: 12, borderRadius: 4,
};

// ─────────────────────────────────────────────────────────────────────
// Modal: Informe Forense detallado (semanal Lun→Dom)
// ─────────────────────────────────────────────────────────────────────
function ForensicReportModal({ report, onClose }) {
  const data = report?.data;
  const r = data;
  const ok = r?.ok;

  const fmtRaw = (n) => {
    const v = Number(n || 0);
    const sign = v > 0 ? "+" : v < 0 ? "−" : "";
    return `${sign}${Math.abs(v).toLocaleString("es-AR")}`;
  };
  const colorOf = (n) => (n > 0 ? "#16a34a" : n < 0 ? "#dc2626" : "#64748b");

  const gradeBadge = (g) => {
    const map = {
      sin_datos: { c: "#94a3b8", t: "📭 Sin datos" },
      muestra_insuficiente: { c: "#eab308", t: "⚠️ Muestra insuficiente" },
      indicio: { c: "#f59e0b", t: "🟡 Indicio" },
      evidencia_media: { c: "#f97316", t: "🟠 Evidencia media" },
      evidencia_fuerte: { c: "#dc2626", t: "🔴 Evidencia fuerte" },
    };
    const x = map[g] || { c: "#64748b", t: g || "—" };
    return (
      <span style={{ background: x.c, color: "#fff", padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700 }}>
        {x.t}
      </span>
    );
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
        zIndex: 60, display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "40px 20px", overflowY: "auto",
      }}
      data-testid="forensic-modal"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "hsl(var(--card))", borderRadius: 12, maxWidth: 1100, width: "100%",
          padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>📋 Informe Forense · Par {report.pair_key}</h2>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              {r?.week?.label || "—"} · UTC
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onClose} data-testid="btn-close-forensic">✕ Cerrar</Button>
        </div>

        {report.loading && <div style={{ padding: 30, textAlign: "center" }}>⏳ Generando informe…</div>}
        {report.error && <div style={{ color: "#dc2626" }}>{report.error}</div>}
        {!report.loading && !ok && <div style={{ color: "#dc2626" }}>{r?.error || "Error"}</div>}

        {ok && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* ⭐ Alertas del Brain para este par (mismo estilo Dashboard) */}
            <PairBrainAlertsSection alerts={report.alerts} loading={report.alerts_loading} report={report.data} />

            {/* Header info */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              {gradeBadge(r.evidence_grade)}
              {r.verdict && (
                <span style={{ fontSize: 11, color: "#64748b" }}>
                  Veredicto del cross-check: <strong>{r.verdict}</strong>
                </span>
              )}
              <span style={{ fontSize: 11, color: "#64748b" }}>
                UID A: <strong>{r.uid_a}</strong> · UID B: <strong>{r.uid_b}</strong>
              </span>
            </div>

            <div style={{ background: "hsl(var(--muted))", padding: 12, borderRadius: 8, fontSize: 12, lineHeight: 1.5 }}>
              {r.fairness_note}
            </div>

            {/* Resumen ejecutivo */}
            <section>
              <h3 style={{ fontSize: 14, margin: "8px 0" }}>📊 Resumen semanal</h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 10 }}>
                <Metric label="Manos juntas" value={r.summary.hands_together_in_week} />
                <Metric label="Mesas compartidas" value={r.summary.tables_shared_count} />
                <Metric label="Sesiones" value={r.summary.sessions_count} />
                <Metric label="Manos a showdown (ambos)" value={r.summary.showdowns_both} />
                <Metric label="Pot ganado por el par" value={`${r.summary.pair_won_pots} / ${r.summary.hands_together_in_week}`} hint={`${r.summary.win_rate_pair_pct}%`} />
                <Metric label="Pot ganado por terceros" value={r.summary.third_party_won_pots} />
                <Metric label={`Profit UID A (${r.uid_a})`} value={fmtRaw(r.summary.profit_uid_a_raw)} color={colorOf(r.summary.profit_uid_a_raw)} />
                <Metric label={`Profit UID B (${r.uid_b})`} value={fmtRaw(r.summary.profit_uid_b_raw)} color={colorOf(r.summary.profit_uid_b_raw)} />
                <Metric label="Net flow (low→high)" value={fmtRaw(r.summary.net_flow_low_to_high_chips)} color={colorOf(r.summary.net_flow_low_to_high_chips)} />
                <Metric label="Gross flow" value={fmtRaw(r.summary.gross_flow_chips)} />
              </div>
            </section>

            {/* Análisis de colusión (sólo si existe) */}
            {r.collusion_analysis && (
              <section>
                <h3 style={{ fontSize: 14, margin: "8px 0" }}>🔍 Análisis de colusión</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px,1fr))", gap: 10 }}>
                  <Metric label="Chip-dump events" value={r.collusion_analysis.chip_dump_events_count} />
                  <Metric label="Showdown wins A" value={r.collusion_analysis.showdown_wins_a} />
                  <Metric label="Showdown wins B" value={r.collusion_analysis.showdown_wins_b} />
                  <Metric label="Dominance index" value={r.collusion_analysis.dominance_index} hint="0=parejo · 1=unilateral" />
                  <Metric label="Win-rate par vs 3ros" value={`${r.collusion_analysis.win_rate_pair_pct}%`} />
                  <Metric label="BB usado" value={r.collusion_analysis.big_blind_used} />
                  <Metric label="Net flow direction" value={r.collusion_analysis.net_flow_direction} />
                </div>
                {r.collusion_analysis.tags?.length > 0 && (
                  <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {r.collusion_analysis.tags.map(t => (
                      <span key={t} style={{ background: "#fee2e2", color: "#991b1b", padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700 }} data-testid={`tag-${t}`}>
                        🚩 {t}
                      </span>
                    ))}
                  </div>
                )}

                {r.collusion_analysis.top_chip_dump_events?.length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <h4 style={{ fontSize: 12, margin: "6px 0", color: "#64748b" }}>Top chip-dump events (≥20 BB)</h4>
                    <table style={tableStyle} data-testid="table-dump-events">
                      <thead>
                        <tr>
                          <th style={thStyle}>hand_id</th>
                          <th style={thStyle}>Mesa</th>
                          <th style={thStyle}>Dirección</th>
                          <th style={thStyle}>Net (BB)</th>
                          <th style={thStyle}>Pot (BB)</th>
                          <th style={thStyle}>Showdown</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.collusion_analysis.top_chip_dump_events.map(d => (
                          <tr key={d.hand_id}>
                            <td style={tdStyle}><code>{d.hand_id}</code></td>
                            <td style={tdStyle}>{d.table_id}</td>
                            <td style={tdStyle}>{d.direction_low_to_high ? `${r.uid_a < r.uid_b ? r.uid_a : r.uid_b} → ${r.uid_a < r.uid_b ? r.uid_b : r.uid_a}` : `${r.uid_a < r.uid_b ? r.uid_b : r.uid_a} → ${r.uid_a < r.uid_b ? r.uid_a : r.uid_b}`}</td>
                            <td style={{ ...tdStyle, color: d.net_flow_bb > 0 ? "#dc2626" : "#16a34a", fontWeight: 600 }}>{d.net_flow_bb}</td>
                            <td style={tdStyle}>{d.pot_total_bb ?? "—"}</td>
                            <td style={tdStyle}>{d.showdown ? "✓" : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {/* Top manos relevantes (siempre) */}
            {r.top_hands?.length > 0 && (
              <section>
                <h3 style={{ fontSize: 14, margin: "8px 0" }}>🃏 Top 15 manos relevantes (por magnitud direccional)</h3>
                <table style={tableStyle} data-testid="table-top-hands">
                  <thead>
                    <tr>
                      <th style={thStyle}>hand_id</th>
                      <th style={thStyle}>Mesa</th>
                      <th style={thStyle}>Pot</th>
                      <th style={thStyle}>Players</th>
                      <th style={thStyle}>Net (chips)</th>
                      <th style={thStyle}>Profit A</th>
                      <th style={thStyle}>Profit B</th>
                      <th style={thStyle}>Ganó</th>
                      <th style={thStyle}>Showdown</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.top_hands.map(h => (
                      <tr key={h.hand_id}>
                        <td style={tdStyle}><code>{h.hand_id}</code></td>
                        <td style={tdStyle}>{h.table_id}</td>
                        <td style={tdStyle}>{h.pot_total_chips?.toLocaleString("es-AR") ?? "—"}</td>
                        <td style={tdStyle}>{h.num_players ?? "—"}</td>
                        <td style={{ ...tdStyle, color: colorOf(h.net_flow_low_to_high), fontWeight: 600 }}>{fmtRaw(h.net_flow_low_to_high)}</td>
                        <td style={{ ...tdStyle, color: colorOf(h.profit_a_raw) }}>{fmtRaw(h.profit_a_raw)}</td>
                        <td style={{ ...tdStyle, color: colorOf(h.profit_b_raw) }}>{fmtRaw(h.profit_b_raw)}</td>
                        <td style={tdStyle}>{h.winner_role}</td>
                        <td style={tdStyle}>
                          {h.showdown ? (
                            <span title={`${h.a_hand_label || "—"} / ${h.b_hand_label || "—"}`}>✓</span>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            {/* Sesiones */}
            {r.summary.sessions?.length > 0 && (
              <section>
                <h3 style={{ fontSize: 14, margin: "8px 0" }}>🕐 Sesiones compartidas (mesa + 30min window)</h3>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Mesa</th>
                      <th style={thStyle}>Inicio</th>
                      <th style={thStyle}>Fin</th>
                      <th style={thStyle}>Manos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.summary.sessions.map((s, i) => (
                      <tr key={i}>
                        <td style={tdStyle}>{s.table_id}</td>
                        <td style={tdStyle}>{new Date(s.first_ts).toLocaleString("es-AR")}</td>
                        <td style={tdStyle}>{new Date(s.last_ts).toLocaleString("es-AR")}</td>
                        <td style={tdStyle}>{s.hands_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers de styling del informe
const Metric = ({ label, value, hint, color }) => (
  <div style={{ background: "hsl(var(--muted))", padding: 10, borderRadius: 8 }}>
    <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 700, color: color || "inherit", fontFamily: "monospace" }}>{value ?? "—"}</div>
    {hint && <div style={{ fontSize: 10, color: "#94a3b8" }}>{hint}</div>}
  </div>
);
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 11 };
const thStyle = { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid hsl(var(--border))", color: "#64748b", fontWeight: 600 };
const tdStyle = { padding: "5px 8px", borderBottom: "1px dashed hsl(var(--border))", fontFamily: "monospace" };

// ─── Sección de Alertas del Brain del par (formato Dashboard inline) ──────
const SEV_COLOR = {
  critical: { bg: "#fef2f2", bd: "#fecaca", fg: "#991b1b", chip: "#dc2626" },
  high:     { bg: "#fff7ed", bd: "#fed7aa", fg: "#9a3412", chip: "#ea580c" },
  medium:   { bg: "#fefce8", bd: "#fef08a", fg: "#854d0e", chip: "#ca8a04" },
  low:      { bg: "#f0fdf4", bd: "#bbf7d0", fg: "#166534", chip: "#16a34a" },
};

// Narrativa dinámica cuando no hay alerts del Brain (detector no activado)
function buildDynamicNarrative(report) {
  if (!report || !report.ok) return null;
  const s = report.summary || {};
  const handsTogether = s.hands_together_in_week || 0;
  const verdict = report.verdict || "unknown";
  const grade = report.evidence_grade || "baja";

  // Si no tenemos NI verdict NI manos → no hay nada que contar
  if (handsTogether === 0 && (!verdict || verdict === "unknown")) return null;

  // Severidad base por verdict + grade
  let severity = "low";
  if (verdict === "collusion_suspected") {
    severity = grade === "alta" ? "critical" : grade === "media" ? "high" : "medium";
  } else if (verdict === "bot_suspected") {
    severity = grade === "alta" ? "high" : "medium";
  } else if (verdict === "legitimate_multi_account") {
    severity = "low";
  }

  // ⭐ Si hay verdict pero NO hay manos en esta semana → narrativa "marcado sin actividad"
  if (handsTogether === 0) {
    const verdictLabelEmpty = {
      collusion_suspected: "colusión sospechada",
      bot_suspected: "bot sospechado",
      legitimate_multi_account: "multi-cuenta legítimo",
      weak_signal: "señal débil",
      no_data: "sin datos",
    }[verdict] || verdict;
    // Bajamos severity un nivel porque no hay actividad en la ventana
    const downgraded = { critical: "high", high: "medium", medium: "low", low: "low" }[severity] || "low";
    const linesEmpty = [];
    linesEmpty.push(`Este par está **marcado por el cross-check de dispositivos vinculados** con veredicto \`${verdictLabelEmpty}\` (grado de evidencia: **${grade}**).`);
    linesEmpty.push("");
    linesEmpty.push(`⚠️ **No hay manos compartidas capturadas en la ventana semanal actual** (Mon→Sun UTC).`);
    linesEmpty.push("");
    linesEmpty.push(`Esto puede deberse a:`);
    linesEmpty.push(`• Los UIDs no jugaron juntos esta semana (pueden haber jugado en semanas anteriores).`);
    linesEmpty.push(`• Las manos cayeron fuera del horizonte de captura del Collector.`);
    linesEmpty.push(`• Uno de los UIDs está actualmente inactivo o restringido.`);
    if (report.fairness_note) {
      linesEmpty.push("");
      linesEmpty.push(`*Nota de fairness:* ${report.fairness_note}`);
    }
    return {
      severity: downgraded,
      title: `Par marcado · sin actividad en esta semana`,
      summary_md: linesEmpty.join("\n"),
      action: "Probá cambiar a la semana anterior (botón ⏪) o revisá el histórico del par. La marca del cross-check sigue vigente — vale la pena observarlo en próximas semanas.",
      tags: ["linked-devices", verdict, `grade-${grade}`, "sin-actividad-semanal"].filter(Boolean),
    };
  }

  const winRate = s.win_rate_pair_pct || 0;
  const profA = Math.round(s.profit_uid_a_raw || 0);
  const profB = Math.round(s.profit_uid_b_raw || 0);
  const netFlow = Math.round(s.net_flow_low_to_high_chips || 0);
  const grossFlow = Math.round(s.gross_flow_chips || 0);
  const tablesShared = s.tables_shared_count || 0;
  const sessions = s.sessions_count || 0;
  const showdowns = s.showdowns_both || 0;
  const pairWon = s.pair_won_pots || 0;
  const thirdWon = s.third_party_won_pots || 0;
  const aWins = s.a_wins || 0;
  const bWins = s.b_wins || 0;

  const fmt = (n) => Number(n).toLocaleString("es-AR");

  const verdictLabel = {
    collusion_suspected: "colusión sospechada",
    bot_suspected: "bot sospechado",
    legitimate_multi_account: "multi-cuenta legítimo",
    weak_signal: "señal débil",
    no_data: "sin datos",
  }[verdict] || verdict;

  const lines = [];
  lines.push(`Este par fue marcado por el cross-check de **dispositivos vinculados** con veredicto \`${verdictLabel}\` (grado de evidencia: **${grade}**).`);
  lines.push("");
  lines.push(`**Convivencia en la ventana semanal:** ${handsTogether} manos juntas a través de ${tablesShared} mesa(s) en ${sessions} sesión(es).`);

  if (pairWon > 0 || thirdWon > 0) {
    lines.push(`En esas manos, el par se llevó ${pairWon} bote(s) entre los dos (win rate combinado ${winRate.toFixed(1)}%) y terceros se llevaron ${thirdWon}.`);
  }
  if (aWins + bWins > 0) {
    lines.push(`Reparto interno: UID ${report.uid_a} ganó ${aWins} mano(s), UID ${report.uid_b} ganó ${bWins}.`);
  }
  if (showdowns > 0) {
    lines.push(`Hubo **${showdowns} showdown(s)** con ambos llegando al río — útil para revelar cartas en informes forenses.`);
  } else {
    lines.push(`Ningún showdown con ambos jugadores llegando al río en esta semana.`);
  }

  lines.push("");
  lines.push(`**Profit raw por jugador (semana):** UID ${report.uid_a}: ${profA >= 0 ? "+" : ""}${fmt(profA)} chips · UID ${report.uid_b}: ${profB >= 0 ? "+" : ""}${fmt(profB)} chips.`);
  if (grossFlow > 0) {
    lines.push(`**Flujo de fichas:** ${fmt(grossFlow)} chips brutos circularon entre ellos (neto direccional: ${netFlow >= 0 ? "+" : ""}${fmt(netFlow)} chips del UID menor al UID mayor).`);
  }

  if (report.fairness_note) {
    lines.push("");
    lines.push(`*Nota de fairness:* ${report.fairness_note}`);
  }

  const actions = {
    critical: "Escalar inmediatamente: revisar manos top, congelar saldos si corresponde y abrir caso formal en Casos Confirmados.",
    high: "Revisar las 15 manos top semanales y cruzar con la semana anterior. Si el patrón persiste → escalar.",
    medium: "Vigilar el par durante 2 semanas más. Si reincide con misma direccionalidad → escalar.",
    low: "Mantener en watchlist. La muestra es chica o la evidencia es indirecta.",
  };

  return {
    severity,
    title: `Par con vínculo de dispositivos · veredicto ${verdictLabel}`,
    summary_md: lines.join("\n"),
    action: actions[severity],
    tags: ["linked-devices", verdict, `grade-${grade}`, `${handsTogether}-manos`].filter(Boolean),
  };
}

function PairBrainAlertsSection({ alerts, loading, report }) {
  const [expanded, setExpanded] = useState({});

  if (loading) {
    return (
      <section data-testid="pair-brain-alerts" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#475569" }}>⏳ Cargando alertas del Brain…</div>
      </section>
    );
  }

  if (!alerts || alerts.length === 0) {
    const dynamic = buildDynamicNarrative(report);
    if (!dynamic) {
      return (
        <section data-testid="pair-brain-alerts" style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 14 }}>🧠</span>
            <strong style={{ fontSize: 13, color: "#475569" }}>Alertas del Brain</strong>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>· sin datos suficientes para narrativa</span>
          </div>
        </section>
      );
    }
    const sev = SEV_COLOR[dynamic.severity] || SEV_COLOR.medium;
    return (
      <section data-testid="pair-brain-alerts" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <span style={{ fontSize: 16 }}>🧠</span>
          <strong style={{ fontSize: 14 }}>Análisis del Brain</strong>
          <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 4, background: "#dbeafe", color: "#1e40af", fontWeight: 600 }}>auto-generado</span>
          <span style={{ fontSize: 11, color: "#64748b" }}>· narrativa basada en datos del informe</span>
        </div>
        <div data-testid="pair-alert-dynamic" style={{ background: sev.bg, border: `1px solid ${sev.bd}`, borderRadius: 8, overflow: "hidden" }}>
          <div style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ background: sev.chip, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
              {dynamic.severity}
            </span>
            <span style={{ fontWeight: 600, fontSize: 13, color: sev.fg, flex: 1, minWidth: 200 }}>
              {dynamic.title}
            </span>
          </div>
          <div style={{ padding: "4px 14px 14px 14px", borderTop: `1px solid ${sev.bd}` }}>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", background: "#fff", padding: 10, borderRadius: 6, border: "1px solid #e2e8f0", marginBottom: 8 }}>
              {dynamic.summary_md}
            </div>
            {dynamic.action && (
              <div style={{ marginTop: 6, padding: 10, background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 6, fontSize: 12 }}>
                <strong>🎯 Acción recomendada:</strong> {dynamic.action}
              </div>
            )}
            {dynamic.tags && dynamic.tags.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                {dynamic.tags.map((t) => (
                  <span key={t} style={{ background: "#e2e8f0", color: "#334155", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                    #{t}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="pair-brain-alerts" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>🧠</span>
        <strong style={{ fontSize: 14 }}>Alertas del Brain ({alerts.length})</strong>
        <span style={{ fontSize: 11, color: "#64748b" }}>· mismas que aparecen en el Dashboard, filtradas por este par</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {alerts.map((a) => {
          const sev = SEV_COLOR[a.severity] || SEV_COLOR.medium;
          const isOpen = !!expanded[a.alert_id];
          return (
            <div
              key={a.alert_id}
              data-testid={`pair-alert-${a.alert_id}`}
              style={{ background: sev.bg, border: `1px solid ${sev.bd}`, borderRadius: 8, overflow: "hidden" }}
            >
              <div
                onClick={() => setExpanded((x) => ({ ...x, [a.alert_id]: !x[a.alert_id] }))}
                style={{ padding: "10px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
              >
                <span style={{ background: sev.chip, color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                  {a.severity}
                </span>
                <span style={{ fontWeight: 600, fontSize: 13, color: sev.fg, flex: 1, minWidth: 200 }}>
                  {a.narrative_title || a.alert_type}
                </span>
                <span style={{ fontSize: 10, color: "#64748b", fontFamily: "monospace" }}>
                  risk {a.risk_score} · conf {a.confidence_score}%
                </span>
                <span style={{ fontSize: 11, color: "#64748b" }}>{isOpen ? "▲" : "▼"}</span>
              </div>
              {isOpen && (
                <div style={{ padding: "4px 14px 14px 14px", borderTop: `1px solid ${sev.bd}` }}>
                  <div style={{ fontSize: 10, fontFamily: "monospace", color: "#64748b", marginBottom: 8 }}>
                    {a.alert_type} · {a.decision} · grade {a.evidence_grade}
                  </div>
                  {a.narrative_summary_md && (
                    <div style={{ fontSize: 12.5, lineHeight: 1.55, whiteSpace: "pre-wrap", background: "#fff", padding: 10, borderRadius: 6, border: "1px solid #e2e8f0", marginBottom: 8 }}>
                      {a.narrative_summary_md}
                    </div>
                  )}
                  {a.narrative_action && (
                    <div style={{ marginTop: 6, padding: 10, background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 6, fontSize: 12 }}>
                      <strong>🎯 Acción recomendada:</strong> {a.narrative_action}
                    </div>
                  )}
                  {a.narrative_tags?.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {a.narrative_tags.map((t) => (
                        <span key={t} style={{ background: "#e2e8f0", color: "#334155", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                  {a.sample_hand_ids?.length > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
                        📋 Manos de muestra ({a.sample_hand_ids.length})
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {a.sample_hand_ids.slice(0, 10).map((hid) => (
                          <a
                            key={hid}
                            href={`/collector-v2/manos?hand_id=${encodeURIComponent(hid)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontFamily: "monospace", fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#f1f5f9", color: "#0ea5e9", textDecoration: "none" }}
                          >
                            {hid}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {a.evidence && (
                    <details style={{ marginTop: 10 }}>
                      <summary style={{ cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#475569" }}>🔬 Evidencia raw (JSON)</summary>
                      <pre style={{ marginTop: 6, padding: 10, fontSize: 10, background: "#0f172a", color: "#cbd5e1", borderRadius: 6, overflow: "auto", maxHeight: 240 }}>
{JSON.stringify(a.evidence, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

